import { useState, useEffect } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { MapPin, Check, Globe, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import citiesData from "@/data/us-cities.json";

// Load US cities from JSON file
const US_CITIES = citiesData.map((city: any) => ({
  city: city.city,
  state: city.stateCode,
  label: `${city.city}, ${city.state}`,
  population: city.population,
}));

const SPECIAL_OPTIONS = [
  { value: "remote", label: "Remote", icon: Globe },
  { value: "united-states", label: "United States (All)", icon: Home },
];

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function CityAutocomplete({ value, onChange, placeholder = "Enter city..." }: CityAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredCities = search.length >= 2
    ? US_CITIES.filter((city) =>
        city.label.toLowerCase().includes(search.toLowerCase())
      )
        .sort((a, b) => b.population - a.population)
        .slice(0, 20)
    : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className={cn(!value && "text-muted-foreground")}>
              {value || placeholder}
            </span>
          </div>
          <Check className={cn("h-4 w-4 shrink-0 opacity-0", value && "opacity-100")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search cities..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No cities found.</CommandEmpty>
            <CommandGroup heading="Special Options">
              {SPECIAL_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onChange(option.label);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {filteredCities.length > 0 && (
              <CommandGroup heading="Cities">
                {filteredCities.map((city) => (
                  <CommandItem
                    key={`${city.city}-${city.state}`}
                    value={city.label}
                    onSelect={() => {
                      onChange(city.label);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                    {city.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
